import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import React, { useMemo, useState, useEffect } from "react";
import { graphql, HeadProps, PageProps } from "gatsby";
import {
  SvCruiseSegmentFragment,
  SitePageContext,
  NewSpecialVoyageQuery
} from "types/graphql-types";
import {
  SectionContainer,
  ProductTypeColor,
  InclusionContent,
  Button,
  Price
} from "@silversea-ssc/ui-components-react";
import {
  buildCruiseName,
  cruiseIsWaitlist,
  cruiseTypeTransformer
} from "src/utils/cruise";
import SectionItineraryContent from "../page-components/special-voyage/SectionItineraryContent";
import SpecialVoyageInfoSummary from "src/page-components/cruise/CruiseInfoSummary/SpecialVoyageInfoSummary";
import useCruisePrice from "src/ssc-api/prices/useCruisePrice";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { getCruiseSpecificRAQ } from "src/utils/requestAQuoteHelper";
import { voyageToDataLayer } from "src/utils/analytics/tracking";
import { arrayIsNotEmpty, isPresent } from "src/utils/checks";
import PrePostProgrammes from "src/page-components/cruise/PrePostProgrammes/PrePostProgrammes";
import { useECommerceStatus } from "src/ssc-api/useECommerceStatus";
import type { FareCode } from "@silversea-ssc/web-fares";
import { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups";
import { PageContext } from "types/pageContext";
import { languageFromCode } from "src/utils/localization/languages/languages";
import GatsbyHeadOpenGraph from "components/SEO/GatsbyHeadOpenGraph";
import { buildSEOImageUrl } from "components/SEO/utils";
import { useSendUserEvent } from "src/utils/useSendUserEvent";
import { activePromosForCruise, activePromosVisibleIn } from "src/utils/promos";
import MapSection from "../page-components/special-voyage/MapSection";
import WhySilverseaSection from "../page-components/special-voyage/WhySilverseaSection/WhySilverseaSection";
import ShipSection from "../page-components/special-voyage/ShipSection";
import BrochurePageBox from "components/BrochurePageBox/BrochurePageBox";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { useTargetBrochure } from "src/utils/brochures";
import OnboardEnrichments from "../page-components/special-voyage/OnboardEnrichments";
import StickyCruiseMenuNew from "src/page-components/cruise/StickyCruiseMenu/StickyCruiseMenuNew";
import PageBuilderBlogMosaic from "components/PageBuilder/PageBuilderBlogMosaic";
import SpecialVoyageItinerary from "../page-components/special-voyage/SpecialVoyageItinerary";
import {
  buildMenuSections,
  getParentPath
} from "../page-components/special-voyage/utils";
import { buildCruiseJsonLd } from "src/page-components/cruise/schemaOrgJsonLd";
import CruiseFares from "components/fare/CruiseFares/CruiseFares";
import { getShorexesActiveOnFares } from "src/page-components/cruise/helpers";
import { ExcursionsMicroContentProvider } from "src/page-components/cruise/ExcursionsMicroContentProvider";
import { buildCruisePageVisited } from "@silversea-ssc/dotcom-api-types/UserEvents";
import { hasMandatoryFields } from "../utils/pre-post-programme";
import {
  getDestinationNameFromComboType,
  getSpecialVoyageDestinationIdFromComboType
} from "src/utils/cruise-tracking";

const TravelDeeperSection = React.lazy(
  () => import("src/page-components/special-voyage/TravelDeeperSection")
);

const CruiseEvents = React.lazy(
  () => import("src/page-components/cruise/cruise-events/CruiseEvents")
);

const ShipSuiteModal = React.lazy(
  () => import("components/ShipFacilityModal/ShipSuiteModal")
);

export type CruiseSegment = SvCruiseSegmentFragment;

interface CruiseSuiteOpenModalArgs {
  type: string;
  suiteCode: string;
  prices: Price[];
  raqCTAUrl?: string;
}

const indexPageTitle = {
  Grand: msg`ALL GRAND VOYAGES`,
  World: msg`ALL WORLD CRUISES`
};

// NOTE: a cruise page without data, destination and code is not generated
export type SpecialVoyageQueryPageProps = NewSpecialVoyageQuery & {
  cruise: NonNullable<NewSpecialVoyageQuery["cruise"]> & {
    data: NonNullable<NonNullable<NewSpecialVoyageQuery["cruise"]>["data"]> & {
      cruiseCode: string;
      cruiseType: "Classic" | "Expedition";
    };
  };
};

const NewSpecialVoyage: React.FC<
  React.PropsWithChildren<
    PageProps<SpecialVoyageQueryPageProps, SitePageContext>
  >
> = (props) => {
  const { i18n } = useLingui();
  const cruise = props.data.cruise;
  const cruiseData = cruise.data;
  const cruiseCode = cruiseData.cruiseCode;

  const cruiseTags = cruise.cruiseTags
    .map((tag) => tag.localized)
    .filter(isPresent);

  const parentPath = getParentPath(props.pageContext);
  const language = useLanguage();

  const [openedModal, setOpenModal] = useState<
    CruiseSuiteOpenModalArgs | null | undefined
  >(null);

  const cruiseName = buildCruiseName(
    cruise.data.departurePort?.data?.name?.localized,
    cruise.data.arrivalPort?.data?.name?.localized
  );
  const cruisePrice = useCruisePrice(cruiseCode, language);
  const cruiseSlideshow = cruise.slideshow?.filter(isPresent);
  const cruiseEvents = cruise.events?.filter(isPresent);
  const price = cruisePrice?.priceFrom;
  const preHotelLand =
    cruiseData.preHotelLand?.filter(isPresent).filter(hasMandatoryFields) || [];
  const postHotelLand =
    cruiseData.postHotelLand?.filter(isPresent).filter(hasMandatoryFields) ||
    [];
  const onboardImages = cruise.onboard?.images?.filter(isPresent);
  const geolocation = useGeolocation();
  const brochures =
    cruise.brochure?.brochureGroup?.brochures?.filter(isPresent);
  const brochure = useTargetBrochure(brochures, geolocation, language);

  const hasExcursions = true;
  const blogSectionContent = cruise.blog;

  const ship = cruiseData?.ship;
  const shipVersion = cruiseData?.shipVersion;

  const withOnboard = arrayIsNotEmpty(onboardImages);
  const withEvents = arrayIsNotEmpty(cruiseEvents);
  const withPrePost =
    arrayIsNotEmpty(preHotelLand) || arrayIsNotEmpty(postHotelLand);
  const withStories =
    isPresent(blogSectionContent) &&
    arrayIsNotEmpty(blogSectionContent.blogPosts);
  const withBrochure = isPresent(brochure);
  const withTravelDeeper = arrayIsNotEmpty(cruise.travelDeeper?.items);
  const withShip = isPresent(ship);

  const menuSections = buildMenuSections({
    hasExcursions,
    withOnboard,
    withEvents,
    withShip,
    withPrePost,
    withStories,
    withBrochure
  });

  const validCruises =
    cruise.data?.cruises?.filter((s): s is SvCruiseSegmentFragment =>
      isPresent(s?.data?.cruiseCode)
    ) || [];

  const comboType = cruiseData.comboType;
  const raqPagePath = props.data.raqPagePath;
  const cruiseSpecificRAQUrl =
    cruiseCode && raqPagePath
      ? getCruiseSpecificRAQ(raqPagePath, cruiseCode)
      : undefined;

  const ecommerceStatus = useECommerceStatus(
    cruiseData.cruiseCode,
    cruiseIsWaitlist(cruisePrice)
  );

  const shorexesActiveOnFares = useMemo(() => {
    return getShorexesActiveOnFares(cruisePrice?.fares || []);
  }, [cruisePrice?.fares]);

  useEffect(() => {
    voyageToDataLayer({
      name: cruiseName,
      code: cruiseCode,
      departure_date: cruiseData?.departureDate,
      arrival_date: cruiseData?.arrivalDate,
      days_duration: cruiseData?.days,
      ship_name: cruiseData?.ship?.name,
      price: price?.type == "price" ? price?.amount : undefined,
      sale_state: price?.type,
      departure_port: cruiseData?.departurePort?.data?.code,
      arrival_port: cruiseData?.arrivalPort?.data?.code,
      destination_id: getSpecialVoyageDestinationIdFromComboType(
        cruiseData.comboType
      ),
      destination_name: getDestinationNameFromComboType(cruiseData.comboType),
      cruise_type: cruiseData?.comboType
    });
  }, [
    cruiseName,
    price,
    cruiseCode,
    cruiseData?.departureDate,
    cruiseData?.days,
    cruiseData?.ship?.name,
    cruiseData?.arrivalDate,
    cruiseData?.departurePort?.data?.code,
    cruiseData?.arrivalPort?.data?.code,
    cruiseData.comboType
  ]);

  const inclusionsContent = (cruise.inclusionsContent?.filter(isPresent) ||
    []) as InclusionContent[];

  useSendUserEvent(buildCruisePageVisited(cruiseCode));

  const activePromos = useMemo(
    () =>
      activePromosForCruise(
        cruisePrice?.webPromotions || [],
        props.data.promos.nodes
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(cruisePrice?.webPromotions)]
  );

  const activePromosForCruiseContent = activePromosVisibleIn(
    activePromos,
    "cruiseFareTab"
  );

  return (
    <React.Fragment>
      <ExcursionsMicroContentProvider
        content={props.data.excursionsMicroContent}
      >
        <ProductTypeColor
          productType={cruiseTypeTransformer(cruise.data.cruiseType)}
        >
          <SpecialVoyageInfoSummary
            cruiseCode={cruiseCode}
            heroImage={cruise.heroImage}
            cruiseMarketingName={cruise.name?.localized}
            cruiseName={cruiseName}
            prices={cruisePrice}
            indexPage={{
              url: parentPath,
              title: comboType ? i18n._(indexPageTitle[comboType]) : undefined
            }}
            departureDate={cruiseData.departureDate}
            arrivalDate={cruiseData.arrivalDate}
            duration={cruiseData.days}
            shipName={cruiseData.ship?.name}
            raqUrl={cruiseSpecificRAQUrl}
            ecommerceStatus={ecommerceStatus}
            activePromos={activePromosForCruiseContent}
            eCommerceBenefitsContent={
              props.data.microContent?.eCommerceBenefitsContent
            }
            cruiseTags={cruiseTags}
          />

          <StickyCruiseMenuNew
            sectionRefs={menuSections}
            spacer={false}
            translateDesktopTop={48}
            summary={{
              cruiseName: cruiseName,
              cruiseCode: cruiseCode,
              prices: cruisePrice,
              departureDate: cruiseData.departureDate,
              arrivalDate: cruiseData.arrivalDate,
              days: cruiseData.days,
              raqUrl: cruiseSpecificRAQUrl,
              ecommerceStatus
            }}
          >
            Upper menu
          </StickyCruiseMenuNew>

          <MapSection
            title={cruise.introTextTitle?.localized}
            content={cruise.introText?.localized}
            map={cruise.map}
          />

          <SectionItineraryContent
            slideshow={cruiseSlideshow}
            uniqueCountriesCount={cruise.uniqueCountriesCount}
            uniquePortsCount={cruise.uniquePortsCount}
            routesCount={validCruises?.length}
            backgroundColor="titanium"
            title={
              cruise.itineraryDescriptionTitle?.localized ||
              cruise.name?.localized
            }
            description={cruise?.itineraryDescription?.localized}
            brochureUrl={cruise.brochure?.brochureGroup?.fullPath}
            ecommerceStatus={ecommerceStatus}
          />

          <div id="fares">
            <CruiseFares
              backgroundColor="neutral10"
              cruiseCode={cruiseCode}
              cancellationPolicyModalContent={
                props.data.cancellationPolicyModalContent!
              }
              inclusionsContent={inclusionsContent}
              cruisePrices={cruisePrice}
              faresContent={props.data.faresContent.nodes}
              cruiseGroup={cruiseData.cruiseGroup as CruiseGroupCode}
              fareCodes={cruiseData.fareCodes as FareCode[]}
              //cruisePackages={infographicCruisePackages}
              sectionHeading={props.data.microContent?.fareSection}
              raqUrl={cruiseSpecificRAQUrl}
              ecommerceStatus={ecommerceStatus}
              farePricesValidUntilDate={cruise.farePricesValidUntilDate}
              farePricesMayIncreaseOnDate={cruise.farePricesMayIncreaseOnDate}
              activePromos={activePromosForCruiseContent}
              pricesValidUntilContent={
                props.data.microContent?.pricesValidUntil?.localized
              }
            />
          </div>

          <div id="itinerary">
            <SpecialVoyageItinerary
              title={
                hasExcursions ? (
                  <Trans>Itinerary & Excursions</Trans>
                ) : (
                  <Trans>Itinerary</Trans>
                )
              }
              cardBackgroundColor="titanium"
              cruiseSegments={validCruises}
              mapSegments={cruise.segments}
              defaultPortImage={props.data.site?.defaultPortImage}
              shorexesActiveOnFares={shorexesActiveOnFares}
              itineraryDisclaimer={
                props.data.microContent?.itineraryShorexDisclaimer?.localized
              }
            />
          </div>

          {withOnboard && (
            <SectionContainer
              backgroundColor="titanium"
              spacingTop="compact"
              spacingBottom="regular"
              dataContentName="onboard"
              id="onboard"
            >
              <OnboardEnrichments
                images={onboardImages}
                content={cruise.onboard?.content?.localized}
              />
            </SectionContainer>
          )}

          {withTravelDeeper && (
            <React.Suspense>
              <TravelDeeperSection
                title={cruise.travelDeeper?.title?.localized}
                content={cruise.travelDeeper?.content?.localized}
                items={cruise?.travelDeeper?.items?.filter(isPresent) || []}
              />
            </React.Suspense>
          )}
          {withEvents && (
            <React.Suspense>
              <CruiseEvents
                id="events"
                events={cruiseEvents}
                backgroundColor="white"
                title={props.data.microContent?.eventsSection?.title?.localized}
                content={
                  comboType == "World"
                    ? props.data.microContent?.eventsSectionWCContent?.localized
                    : props.data.microContent?.eventsSectionGVContent?.localized
                }
              />
            </React.Suspense>
          )}

          <WhySilverseaSection
            id="benefits"
            title={
              comboType == "World"
                ? props.data.microContent?.worldCruiseWhySilverseaSection?.title
                    ?.localized
                : props.data.microContent?.grandVoyageWhySilverseaSection?.title
                    ?.localized
            }
            content={
              comboType == "World"
                ? props.data.microContent?.worldCruiseWhySilverseaSection
                    ?.content?.localized
                : props.data.microContent?.grandVoyageWhySilverseaSection
                    ?.content?.localized
            }
            cruiseFareInclusions={cruise.cruiseFareInclusions?.localized}
            cruiseFareInclusionsFootnotes={
              cruise.cruiseFareInclusionsFootnotes?.localized
            }
          />
          {withShip && shipVersion && (
            <ShipSection
              shipSectionId="ship"
              suitesSectionId="suites"
              ship={ship}
              shipVersion={shipVersion}
              cruisePrice={cruisePrice}
              ecommerceStatus={ecommerceStatus}
              activePromos={activePromos}
              backgroundColor="neutral10"
              cruiseCode={cruiseCode}
              raqPagePath={raqPagePath}
              suiteSectionTitle={
                props.data.microContent?.suitesSection?.title?.localized
              }
              suiteSectionContent={
                cruiseData.comboType == "Grand"
                  ? props.data.microContent?.suitesSectionGVContent?.localized
                  : props.data.microContent?.suitesSectionWCContent?.localized
              }
            />
          )}

          {withPrePost && (
            <div id="prePost">
              <PrePostProgrammes
                preFallbackImage={cruiseData.departurePort?.firstImage}
                postFallbackImage={cruiseData.arrivalPort?.firstImage}
                preProgrammes={preHotelLand}
                postProgrammes={postHotelLand}
              />
            </div>
          )}

          {withStories && (
            <SectionContainer
              spacingTop="regular"
              spacingBottom="regular"
              dataContentName="stories"
              id="stories"
              backgroundColor={withPrePost ? "white" : "neutral05"}
            >
              <PageBuilderBlogMosaic
                content={blogSectionContent}
                cardBackgroundColor={withPrePost ? "neutral05" : "white"}
              />
            </SectionContainer>
          )}

          {withBrochure && (
            <div id="brochure">
              <SectionContainer
                dataContentName="brochure"
                spacingTop="regular"
                spacingBottom="regular"
                backgroundColor="titaniumDark"
              >
                <BrochurePageBox
                  image={brochure.thumbnail}
                  title={
                    cruise.brochure?.title?.localized ||
                    cruise.cruiseAbstract?.localized
                  }
                  content={cruise.brochure?.content?.localized}
                  ctaButton={
                    <Button
                      renderAs="a"
                      href={cruise.brochure?.brochureGroup?.fullPath}
                      iconName="brochure"
                      dataContentPiece="link_to_brochure"
                      inverted
                    >
                      <Trans id="cta-request-single-brochure">
                        Request a brochure
                      </Trans>
                    </Button>
                  }
                  backgroundColor="titanium"
                  titleColor="white"
                />
              </SectionContainer>
            </div>
          )}

          {cruiseData?.ship?.fullPath &&
            openedModal &&
            cruiseData.shipVersion &&
            cruiseData.shipVersion._key && (
              <React.Suspense fallback={<div></div>}>
                <ShipSuiteModal
                  modal={{
                    isOpen: !!openedModal,
                    onRequestClose: () => setOpenModal(null)
                  }}
                  shipPath={cruiseData.ship.fullPath}
                  {...openedModal}
                  shipVersionKey={cruiseData.shipVersion._key}
                />
              </React.Suspense>
            )}
        </ProductTypeColor>
      </ExcursionsMicroContentProvider>
    </React.Fragment>
  );
};

export const Head = (
  props: HeadProps<SpecialVoyageQueryPageProps, PageContext>
) => {
  const {
    data,
    pageContext,
    location: { pathname }
  } = props;
  const language = languageFromCode(pageContext.languageCode);
  const cruise = data.cruise;
  const cruiseData = cruise.data;
  const cruiseCode = cruiseData.cruiseCode;
  const openGraph = cruise.metadata?.openGraph;
  const seoImage = buildSEOImageUrl(openGraph?.image || cruise.card?.image);

  const parentPath = getParentPath(props.pageContext);
  const comboType = cruiseData.comboType;

  const destinationName = {
    Grand: `GRAND VOYAGES`,
    World: `WORLD CRUISES`
  };

  return (
    <GatsbyHeadOpenGraph
      path={props.location.pathname}
      alternateLinks={pageContext.alternateLinks}
      languageCode={pageContext.languageCode}
      openGraph={openGraph}
      excludeFromSitemap={cruise.metadata?.excludeFromSitemap}
      jsonLd={buildCruiseJsonLd({
        language: language,
        image: seoImage,
        name: openGraph?.title?.localized,
        pathname: pathname,
        departureDate: cruiseData.departureDate,
        arrivalDate: cruiseData.arrivalDate,
        departureCityName: cruiseData.departurePort?.data?.name?.localized,
        arrivalCityName: cruiseData.arrivalPort?.data?.name?.localized,
        cruiseCode: cruiseData.cruiseCode,
        description: openGraph?.description?.localized,
        destinationName: comboType ? destinationName[comboType] : undefined,
        destinationUrl: parentPath
      })}
    >
      <link
        rel="prefetch"
        href={`${process.env.GATSBY_DIGITAL_API_BASE_URL}/v2/cruisePriceAndOffers/${cruiseCode}?language=${language.sanityCode}`}
        as="fetch"
        type="application/json"
      />
    </GatsbyHeadOpenGraph>
  );
};

export const query = graphql`
  fragment SVCruiseSegment on SanityCruise {
    _id
    data {
      cruiseCode
      cruiseType
      departureDate
      arrivalDate
      departurePort {
        data {
          code
          name {
            localized
          }
        }
      }
      arrivalPort {
        data {
          code
          name {
            localized
          }
        }
      }

      itinerary {
        ...CruiseItinerary
      }

      map {
        ...Image
      }
    }
    fullPath
  }

  fragment SpecialVoyageSegment on SanitySpecialVoyageSegment {
    cruise {
      _id
    }
    map {
      ...Image
    }
    title {
      localized
    }
  }

  query NewSpecialVoyage($id: String!) {
    raqPagePath
    cruise: sanitySpecialVoyage(id: { eq: $id }) {
      metadata {
        excludeFromSitemap
        openGraph {
          ...OpenGraphMetaTags
        }
      }
      card {
        image {
          ...ImageWithAlt
        }
      }
      name {
        localized
      }

      cruiseAbstract {
        localized
      }

      farePricesValidUntilDate
      farePricesMayIncreaseOnDate

      inclusionsContent {
        ...InfographicStepInclusionContent
      }

      cruiseFareInclusions {
        _key
        localized
      }

      cruiseFareInclusionsFootnotes {
        localized
      }

      heroImage {
        ...HeroResponsiveImage
      }

      brochure {
        title {
          localized
        }
        content {
          localized
        }
        brochureGroup {
          brochures {
            ...PageBuilderBrochureLeadForm_Brochure
          }
          fullPath
        }
      }

      introTextTitle {
        localized
      }
      introText {
        localized
      }
      map {
        ...Image
      }

      uniqueCountriesCount
      uniquePortsCount

      segments {
        ...SpecialVoyageSegment
      }

      onboard {
        content {
          localized
        }
        images {
          ...SlideshowWithVideo
        }
      }

      travelDeeper {
        title {
          localized
        }
        content {
          localized
        }
        items {
          ...PageBuilderCardMosaicItem
        }
      }

      blog {
        ...PageBuilderBlogSection
      }

      itineraryDescriptionTitle {
        localized
      }
      itineraryDescription {
        localized
      }

      slideshow {
        ...SlideshowWithVideo
      }

      cruiseTags {
        localized
      }

      data {
        cruiseCode
        comboType
        cruiseType
        departureDate
        arrivalDate
        days
        cruiseGroup
        fareCodes

        departurePort {
          firstImage {
            ...ImageWithCaption
          }
          data {
            code
            name {
              localized
            }
          }
        }
        arrivalPort {
          firstImage {
            ...ImageWithCaption
          }
          data {
            code
            name {
              localized
            }
          }
        }

        postHotelLand {
          ...HotelOrLandProgramme
        }

        preHotelLand {
          ...HotelOrLandProgramme
        }

        ship {
          ...CruiseSectionShip
        }
        shipVersion {
          ...CruiseSectionShipVersion
        }

        cruises {
          ...SVCruiseSegment
        }
      }
      events {
        ...CruiseEvent
      }
    }

    cancellationPolicyModalContent: sanityCancellationPolicyModal(
      _id: { regex: "/cancellationPolicyModal$/" }
    ) {
      ...CancellationPolicyModalContent
    }

    faresContent: allSanityFarePage {
      nodes {
        ...CruiseFareInfographicContent
      }
    }

    microContent: cruisePageMicroContent {
      eCommerceBenefitsContent {
        ...ECommerceBenefitsContent
      }

      fareSection {
        title {
          localized
        }
        content {
          localized
        }
      }

      pricesValidUntil {
        localized
      }

      itineraryShorexDisclaimer {
        localized
      }

      suitesSection {
        title {
          localized
        }
      }

      suitesSectionGVContent {
        localized
      }

      suitesSectionWCContent {
        localized
      }

      worldCruiseWhySilverseaSection {
        title {
          localized
        }
        content {
          localized
        }
      }

      grandVoyageWhySilverseaSection {
        title {
          localized
        }
        content {
          localized
        }
      }

      eventsSection {
        title {
          localized
        }
      }

      eventsSectionGVContent {
        localized
      }
      eventsSectionWCContent {
        localized
      }
    }

    excursionsMicroContent: excursionsMicroContent {
      ...MicroContentExcursions
    }

    site: sanitySite(_id: { eq: "site-silversea-com" }) {
      defaultPortImage {
        ...ImageWithAlt
      }
    }

    promos: allSanityExclusiveOfferPerPromo {
      nodes {
        ...CruiseOfferContent
      }
    }
  }
`;

export default NewSpecialVoyage;
